import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { forceReload, isChunkLoadError, reloadForChunkError } from '../../lib/chunkReload';
import { logger } from '../../lib/logger';

/**
 * Root `errorElement` for the router — replaces React Router's default
 * "Unexpected Application Error / Hey developer" screen.
 *
 * Must be attached to a route object in createBrowserRouter (App.tsx): an
 * errorElement on a <Route> inside a descendant <Routes> (StudentApp,
 * MentorApp, AdminApp) is silently ignored by React Router.
 *
 * Stale-chunk errors normally never reach here (lazyWithReload catches them);
 * this is the safety net if one slips through.
 */
export default function RouteError() {
  // useSuspense: false — this renders above every Suspense boundary, so it must
  // not suspend while a locale file loads; defaultValue covers that window.
  const { t } = useTranslation('common', { useSuspense: false });
  const error = useRouteError();
  const [reloading] = useState(() => isChunkLoadError(error) && reloadForChunkError(error));

  useEffect(() => {
    if (!reloading) logger.error('[route-error] unhandled route error', error);
  }, [error, reloading]);

  // Auto-reload under way: keep the same spinner as the Suspense fallbacks.
  if (reloading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center bg-surface">
      <svg className="w-10 h-10 text-earth-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
      <div>
        <p className="text-sm font-semibold text-earth-700">
          {t('errors.routeError.title', { defaultValue: 'Failed to load this page' })}
        </p>
        <p className="mt-1 text-xs text-earth-400">
          {t('errors.routeError.hint', { defaultValue: 'A new version may have been deployed. Try reloading.' })}
        </p>
      </div>
      <button
        type="button"
        onClick={forceReload}
        className="h-8 px-4 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors"
      >
        {t('errors.routeError.reload', { defaultValue: 'Reload page' })}
      </button>
    </div>
  );
}
